import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Users, DollarSign, TrendingUp, Award, CheckCircle, 
  Sparkles, Clock, Eye, Copy, FileText, ExternalLink,
  BarChart3, Download, Info, Shield, Lock, Mail, LogOut,
  Link2, Plus, PieChart, Trophy, Image, Video, Folder,
  Send, Trash2, LinkIcon, Loader2, Star, Medal, Crown, Gift, X,
  AlertTriangle, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateProgram = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [customContent, setCustomContent] = useState(null);
  const [settings, setSettings] = useState({
    affiliate_commission_percent: 10,
    affiliate_customer_discount: 5,
    affiliate_cookie_days: 30
  });
  
  // Form states
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    company_name: '',
    website: '', 
    social_media: '',
    marketing_methods: '',
    estimated_monthly_sales: '',
    accepted_terms: false,
    accepted_legal_disclosure: false
  });
  
  // Auth states
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [affiliateInfo, setAffiliateInfo] = useState(null);
  
  // Password setup
  const [showPasswordSetup, setShowPasswordSetup] = useState(false);
  const [passwordToken, setPasswordToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  
  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: email, 2: code, 3: new password
  const [verificationCode, setVerificationCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false); // Keep for URL-based reset compatibility
  
  // UI states
  const [submitting, setSubmitting] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [utmStats, setUtmStats] = useState(null);
  const [trackingLinks, setTrackingLinks] = useState([]);
  const [showLegalDialog, setShowLegalDialog] = useState(null);
  const [legalContent, setLegalContent] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // UTM Link Builder
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [newLink, setNewLink] = useState({
    campaign_name: '',
    source: '',
    medium: '',
    term: '',
    content: ''
  });

  // Short Links
  const [shortLinks, setShortLinks] = useState([]);
  const [showShortLinkBuilder, setShowShortLinkBuilder] = useState(false);
  const [newShortLink, setNewShortLink] = useState({
    custom_slug: '',
    destination_url: '/',
    utm_source: '',
    utm_medium: '',
    utm_campaign: ''
  });
  const [creatingShortLink, setCreatingShortLink] = useState(false);

  // Leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardStats, setLeaderboardStats] = useState(null);
  const [showOnLeaderboard, setShowOnLeaderboard] = useState(false);
  
  // Media Library
  const [mediaAssets, setMediaAssets] = useState([]);
  const [mediaCategories, setMediaCategories] = useState([]);
  const [selectedMediaCategory, setSelectedMediaCategory] = useState('');
  const [mediaRequests, setMediaRequests] = useState([]);
  const [showMediaRequestForm, setShowMediaRequestForm] = useState(false);
  const [newMediaRequest, setNewMediaRequest] = useState({
    request_type: 'specific_asset',
    description: '',
    urgency: 'normal'
  });

  // Campaign System
  const [campaigns, setCampaigns] = useState([]);
  const [tierInfo, setTierInfo] = useState(null);
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    commission_points: 5,
    discount_points: 5,
    description: '',
    target_audience: '',
    is_active: true,
    custom_code: '',
    use_custom_code: false
  });
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignCustomers, setCampaignCustomers] = useState([]);
  const [codeAvailability, setCodeAvailability] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);

  // Opt-in states for application (read-and-check flow)
  const [showOptInModal, setShowOptInModal] = useState(false);
  const [termsRead, setTermsRead] = useState(false);
  const [disclosureRead, setDisclosureRead] = useState(false);
  const [termsContent, setTermsContent] = useState(null);
  const [disclosureContent, setDisclosureContent] = useState(null);
  const [loadingLegalDocs, setLoadingLegalDocs] = useState(false);
  const [expandedOptInSection, setExpandedOptInSection] = useState(null);

  useEffect(() => {
    fetchSettings();
    
    // Check for password setup token in URL
    const setupToken = searchParams.get('setup_password');
    if (setupToken) {
      setPasswordToken(setupToken);
      setShowPasswordSetup(true);
    }
    
    // Check for password reset token in URL
    const resetTokenParam = searchParams.get('reset_token');
    if (resetTokenParam) {
      setResetToken(resetTokenParam);
      setShowResetPassword(true);
    }
    
    // Check for existing session
    const savedToken = localStorage.getItem('affiliate_session');
    if (savedToken) {
      verifySession(savedToken);
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`);
      if (res.data) {
        setSettings({
          affiliate_commission_percent: res.data.affiliate_commission_percent || 10,
          affiliate_customer_discount: res.data.affiliate_customer_discount || 5,
          affiliate_cookie_days: res.data.affiliate_cookie_days || 30
        });
        if (res.data.affiliate_program_content) {
          setCustomContent(res.data.affiliate_program_content);
        }
      }
    } catch (e) {
      console.error('Failed to fetch settings:', e);
    }
  };

  const verifySession = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/verify-session/${token}`);
      if (res.data.valid) {
        setIsLoggedIn(true);
        setSessionToken(token);
        setAffiliateInfo(res.data);
        await fetchDashboard(res.data.email);
        await fetchUTMStats(res.data.email);
        await fetchTrackingLinks(res.data.email);
        await fetchShortLinks(res.data.email);
        await fetchLeaderboard(res.data.email);
        await fetchMediaLibrary();
        await fetchMediaRequests(res.data.email);
        await fetchTierInfo(res.data.email);
        await fetchCampaigns(res.data.email);
      }
    } catch (error) {
      localStorage.removeItem('affiliate_session');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/login`, {
        email: loginEmail,
        password: loginPassword
      });
      
      localStorage.setItem('affiliate_session', res.data.token);
      setSessionToken(res.data.token);
      setIsLoggedIn(true);
      setAffiliateInfo({
        email: res.data.email,
        name: res.data.name,
        affiliate_code: res.data.affiliate_code
      });
      
      await fetchDashboard(res.data.email);
      await fetchUTMStats(res.data.email);
      await fetchTrackingLinks(res.data.email);
      await fetchShortLinks(res.data.email);
      await fetchLeaderboard(res.data.email);
      await fetchMediaLibrary();
      await fetchMediaRequests(res.data.email);
      await fetchTierInfo(res.data.email);
      await fetchCampaigns(res.data.email);
      
      toast.success('Welcome back!');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('set up your password')) {
        setNeedsPasswordSetup(true);
        toast.error('Please set up your password first. Check your email for the setup link.');
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_session');
    setIsLoggedIn(false);
    setSessionToken(null);
    setAffiliateInfo(null);
    setDashboard(null);
    setUtmStats(null);
    setTrackingLinks([]);
    toast.success('Logged out successfully');
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/set-password`, {
        token: passwordToken,
        password: newPassword,
        confirm_password: confirmPassword
      });
      
      toast.success('Password set successfully! You can now log in.');
      setShowPasswordSetup(false);
      setPasswordToken('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Clear URL parameter
      searchParams.delete('setup_password');
      setSearchParams(searchParams);
      
      // Pre-fill login email
      if (res.data.email) {
        setLoginEmail(res.data.email);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendPasswordLink = async () => {
    if (!loginEmail) {
      toast.error('Please enter your email first');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/affiliates/resend-password-link?email=${loginEmail}`);
      toast.success('Password setup link has been sent to your email');
      setNeedsPasswordSetup(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send link');
    }
  };

  // Forgot Password Handler - Step 1: Send verification code
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      setForgotPasswordStep(2); // Move to code entry step
      toast.success('Verification code sent! Check your email.');
      
      // For testing: if verification_code is returned (email not configured), show it
      if (res.data.verification_code) {
        console.log('Verification code (email not sent):', res.data.verification_code);
        toast.info(`Test mode - Code: ${res.data.verification_code}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSubmitting(false);
    }
  };

  // Forgot Password Handler - Step 2: Verify code
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit verification code');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/affiliates/verify-reset-code?email=${encodeURIComponent(forgotPasswordEmail)}&code=${verificationCode}`);
      
      if (res.data.verified) {
        setResetToken(res.data.reset_token);
        setForgotPasswordStep(3); // Move to password entry step
        toast.success('Code verified! Now set your new password.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setSubmitting(false);
    }
  };

  // Forgot Password Handler - Step 3: Set new password
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (resetNewPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/affiliates/reset-password`, {
        token: resetToken,
        new_password: resetNewPassword,
        confirm_password: resetConfirmPassword
      });
      
      toast.success('Password reset successfully! You can now log in.');
      resetForgotPasswordState();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // Reset all forgot password state
  const resetForgotPasswordState = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordEmail('');
    setVerificationCode('');
    setResetToken('');
    setResetNewPassword('');
    setResetConfirmPassword('');
  };

  // Legacy Reset Password Handler (for URL-based reset links)
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (resetNewPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResetPasswordLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/affiliates/reset-password`, {
        token: resetToken,
        new_password: resetNewPassword,
        confirm_password: resetConfirmPassword
      });
      
      toast.success('Password reset successfully! You can now log in.');
      setShowResetPassword(false);
      setResetToken('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      
      // Clear URL parameter
      searchParams.delete('reset_token');
      setSearchParams(searchParams);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    
    // Instead of submitting directly, open the opt-in modal
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    // Open the opt-in modal to read and accept documents
    fetchLegalDocsForOptIn();
    setShowOptInModal(true);
  };

  const fetchDashboard = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/dashboard/${email}`);
      setDashboard(res.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchUTMStats = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/utm-stats/${email}`);
      setUtmStats(res.data);
    } catch (error) {
      console.error('Failed to fetch UTM stats:', error);
    }
  };

  const fetchTrackingLinks = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/tracking-links/${email}`);
      setTrackingLinks(res.data.links || []);
    } catch (error) {
      console.error('Failed to fetch tracking links:', error);
    }
  };

  // Short Links
  const fetchShortLinks = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/short-links/${email}`);
      setShortLinks(res.data.links || []);
    } catch (error) {
      console.error('Failed to fetch short links:', error);
    }
  };

  const handleCreateShortLink = async (e) => {
    e.preventDefault();
    setCreatingShortLink(true);
    
    try {
      const res = await axios.post(
        `${API_URL}/api/affiliates/short-links?affiliate_email=${affiliateInfo.email}`,
        newShortLink
      );
      
      toast.success('Short link created!');
      setShortLinks([{ ...res.data, created_at: new Date().toISOString() }, ...shortLinks]);
      setShowShortLinkBuilder(false);
      setNewShortLink({ custom_slug: '', destination_url: '/', utm_source: '', utm_medium: '', utm_campaign: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create short link');
    } finally {
      setCreatingShortLink(false);
    }
  };

  const handleDeleteShortLink = async (code) => {
    if (!window.confirm('Delete this short link? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/affiliates/short-links/${code}?affiliate_email=${affiliateInfo.email}`);
      setShortLinks(shortLinks.filter(l => l.code !== code));
      toast.success('Short link deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete link');
    }
  };

  // Leaderboard
  const fetchLeaderboard = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/leaderboard?affiliate_email=${email}`);
      setLeaderboard(res.data.leaderboard || []);
      setLeaderboardStats(res.data.your_stats);
      setShowOnLeaderboard(res.data.your_stats?.show_on_leaderboard || false);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const toggleLeaderboardVisibility = async () => {
    try {
      const newValue = !showOnLeaderboard;
      await axios.post(
        `${API_URL}/api/affiliates/leaderboard/opt-in?affiliate_email=${affiliateInfo.email}&show_on_leaderboard=${newValue}`
      );
      setShowOnLeaderboard(newValue);
      toast.success(newValue ? "You're now on the leaderboard!" : "You've been removed from the leaderboard");
      fetchLeaderboard(affiliateInfo.email);
    } catch (error) {
      toast.error('Failed to update preference');
    }
  };

  // Media Library
  const fetchMediaLibrary = async (category = '') => {
    try {
      const url = category 
        ? `${API_URL}/api/affiliates/media-library?category=${category}`
        : `${API_URL}/api/affiliates/media-library`;
      const res = await axios.get(url);
      setMediaAssets(res.data.assets || []);
      setMediaCategories(res.data.categories || []);
    } catch (error) {
      console.error('Failed to fetch media library:', error);
    }
  };

  const fetchMediaRequests = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/media-requests/${email}`);
      setMediaRequests(res.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch media requests:', error);
    }
  };

  const handleSubmitMediaRequest = async (e) => {
    e.preventDefault();
    
    if (!newMediaRequest.description) {
      toast.error('Please describe what you need');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/affiliates/media-requests?affiliate_email=${affiliateInfo.email}`,
        newMediaRequest
      );
      toast.success('Request submitted! Our team will review it shortly.');
      setShowMediaRequestForm(false);
      setNewMediaRequest({ request_type: 'specific_asset', description: '', urgency: 'normal' });
      fetchMediaRequests(affiliateInfo.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  // Campaign Functions
  const fetchTierInfo = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/tier-info/${email}`);
      setTierInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch tier info:', error);
    }
  };

  const fetchCampaigns = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/campaigns/${email}`);
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  };

  const fetchCampaignCustomers = async (campaignId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/affiliates/campaigns/${campaignId}/customers?email=${affiliateInfo.email}`
      );
      setCampaignCustomers(res.data.customers || []);
      setSelectedCampaign(res.data.campaign);
    } catch (error) {
      console.error('Failed to fetch campaign customers:', error);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    setCreatingCampaign(true);
    
    if (!newCampaign.campaign_name) {
      toast.error('Please enter a campaign name');
      setCreatingCampaign(false);
      return;
    }
    
    // Validate custom code if using one
    if (newCampaign.use_custom_code && newCampaign.custom_code) {
      if (codeAvailability === false) {
        toast.error('Please choose an available code');
        setCreatingCampaign(false);
        return;
      }
    }
    
    const totalPoints = newCampaign.commission_points + newCampaign.discount_points;
    const availablePoints = tierInfo?.available_points || 10;
    
    if (totalPoints > availablePoints) {
      toast.error(`Total points (${totalPoints}) cannot exceed your available points (${availablePoints})`);
      setCreatingCampaign(false);
      return;
    }
    
    try {
      const payload = {
        ...newCampaign,
        custom_code: newCampaign.use_custom_code ? newCampaign.custom_code : null
      };
      delete payload.use_custom_code;
      
      await axios.post(
        `${API_URL}/api/affiliates/campaigns/create?email=${affiliateInfo.email}`,
        payload
      );
      
      toast.success('Campaign created successfully!');
      setShowCampaignBuilder(false);
      setNewCampaign({
        campaign_name: '',
        commission_points: 5,
        discount_points: 5,
        description: '',
        target_audience: '',
        is_active: true,
        custom_code: '',
        use_custom_code: false
      });
      setCodeAvailability(null);
      fetchCampaigns(affiliateInfo.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreatingCampaign(false);
    }
  };
  
  const checkCodeAvailability = async (code) => {
    if (!code || code.length < 4) {
      setCodeAvailability(null);
      return;
    }
    
    setCheckingCode(true);
    try {
      const res = await axios.get(`${API_URL}/api/affiliates/campaigns/check-code/${code}`);
      setCodeAvailability(res.data.available);
    } catch (error) {
      setCodeAvailability(null);
    } finally {
      setCheckingCode(false);
    }
  };

  const handleToggleCampaign = async (campaignId, isActive) => {
    try {
      await axios.put(
        `${API_URL}/api/affiliates/campaigns/${campaignId}?email=${affiliateInfo.email}`,
        { ...campaigns.find(c => c.id === campaignId), is_active: !isActive }
      );
      toast.success(`Campaign ${isActive ? 'deactivated' : 'activated'}`);
      fetchCampaigns(affiliateInfo.email);
    } catch (error) {
      toast.error('Failed to update campaign');
    }
  };

  const getTierColor = (tierName) => {
    const colors = {
      'Bronze': 'text-amber-700 bg-amber-100',
      'Silver': 'text-gray-600 bg-gray-200',
      'Gold': 'text-yellow-600 bg-yellow-100',
      'Platinum': 'text-purple-600 bg-purple-100',
      'Diamond': 'text-blue-600 bg-blue-100',
      'Custom': 'text-green-600 bg-green-100'
    };
    return colors[tierName] || 'text-gray-600 bg-gray-100';
  };

  const handleCreateTrackingLink = async (e) => {
    e.preventDefault();
    
    if (!newLink.campaign_name || !newLink.source || !newLink.medium) {
      toast.error('Please fill in campaign name, source, and medium');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API_URL}/api/affiliates/create-tracking-link?email=${affiliateInfo.email}`,
        newLink
      );
      
      toast.success('Tracking link created!');
      setTrackingLinks([res.data.link, ...trackingLinks]);
      setShowLinkBuilder(false);
      setNewLink({ campaign_name: '', source: '', medium: '', term: '', content: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    }
  };

  const fetchLegalDoc = async (type) => {
    try {
      const endpoint = type === 'terms' ? 'terms' : type === 'disclosure' ? 'disclosure' : 'customer-disclosure';
      const res = await axios.get(`${API_URL}/api/affiliates/legal/${endpoint}`);
      setLegalContent(res.data);
      setShowLegalDialog(type);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  // Fetch legal documents for opt-in modal
  const fetchLegalDocsForOptIn = async () => {
    setLoadingLegalDocs(true);
    try {
      const [termsRes, disclosureRes] = await Promise.all([
        axios.get(`${API_URL}/api/affiliates/legal/terms`),
        axios.get(`${API_URL}/api/affiliates/legal/disclosure`)
      ]);
      setTermsContent(termsRes.data);
      setDisclosureContent(disclosureRes.data);
    } catch (error) {
      toast.error('Failed to load legal documents');
    } finally {
      setLoadingLegalDocs(false);
    }
  };

  // Handle opening the opt-in modal
  const handleStartApplication = () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email first');
      return;
    }
    fetchLegalDocsForOptIn();
    setShowOptInModal(true);
  };

  // Handle accepting both documents and submitting application
  const handleAcceptAndApply = async () => {
    if (!termsRead || !disclosureRead) {
      toast.error('Please read and accept both documents');
      return;
    }
    
    setShowOptInModal(false);
    setFormData({ ...formData, accepted_terms: true, accepted_legal_disclosure: true });
    
    // Submit application
    setSubmitting(true);
    try {
      const applicationData = {
        ...formData,
        accepted_terms: true,
        accepted_legal_disclosure: true,
        terms_accepted_at: new Date().toISOString(),
        disclosure_accepted_at: new Date().toISOString()
      };
      const res = await axios.post(`${API_URL}/api/affiliates/apply`, applicationData);
      setApplicationSuccess(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const getReferralLink = (code) => {
    return `${window.location.origin}?ref=${code}`;
  };

  // Password Setup Dialog
  if (showPasswordSetup) {
    return (
      <div className="min-h-screen bg-[#f0f4f8]">
        <Navbar />
        <div className="pt-44 pb-16">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className="text-center mb-6">
                <Lock className="h-12 w-12 text-[#003456] mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-[#0a0203]">Set Your Password</h2>
                <p className="text-gray-600 mt-2">Create a password to access your affiliate dashboard</p>
              </div>
              
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <Input
                    type="password"
                    required
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <Input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#003456]" disabled={submitting}>
                  {submitting ? 'Setting Password...' : 'Set Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-16 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <Award className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Affiliate Program</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Partner with HomeLyph and customize your earnings! Create campaigns with up to 15 marketing credits to split between your commission and customer discounts.
          </p>
        </div>
      </div>
      
      <div className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Logged In - Dashboard View */}
          {isLoggedIn && affiliateInfo ? (
            <div className="animate-fade-in">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-[#0a0203]">Welcome, {affiliateInfo.name}</h2>
                  <p className="text-gray-500">Affiliate Code: <span className="font-mono font-bold text-[#003456]">{affiliateInfo.affiliate_code}</span></p>
                </div>
                <Button variant="outline" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" /> Logout
                </Button>
              </div>

              {/* Quick Copy Section */}
              <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Your Referral Link</p>
                    <code className="text-sm bg-gray-100 px-3 py-1 rounded mt-1 block">
                      {getReferralLink(affiliateInfo.affiliate_code)}
                    </code>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => copyToClipboard(getReferralLink(affiliateInfo.affiliate_code))}>
                      <Copy className="h-4 w-4 mr-1" /> Copy Link
                    </Button>
                    <Button variant="outline" onClick={() => copyToClipboard(affiliateInfo.affiliate_code)}>
                      <Copy className="h-4 w-4 mr-1" /> Copy Code
                    </Button>
                  </div>
                </div>
              </div>

              {/* Dashboard Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-6 bg-white p-1 rounded-lg shadow-sm flex-wrap">
                  <TabsTrigger value="overview" className="px-4 py-2">
                    <BarChart3 className="h-4 w-4 mr-2" /> Overview
                  </TabsTrigger>
                  <TabsTrigger value="short-links" className="px-4 py-2" data-testid="short-links-tab">
                    <LinkIcon className="h-4 w-4 mr-2" /> Short Links
                  </TabsTrigger>
                  <TabsTrigger value="campaigns" className="px-4 py-2">
                    <PieChart className="h-4 w-4 mr-2" /> Campaigns
                  </TabsTrigger>
                  <TabsTrigger value="leaderboard" className="px-4 py-2" data-testid="leaderboard-tab">
                    <Trophy className="h-4 w-4 mr-2" /> Leaderboard
                  </TabsTrigger>
                  <TabsTrigger value="media" className="px-4 py-2" data-testid="media-tab">
                    <Image className="h-4 w-4 mr-2" /> Media Library
                  </TabsTrigger>
                  <TabsTrigger value="links" className="px-4 py-2">
                    <Link2 className="h-4 w-4 mr-2" /> UTM Links
                  </TabsTrigger>
                  <TabsTrigger value="legal" className="px-4 py-2">
                    <FileText className="h-4 w-4 mr-2" /> Legal
                  </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview">
                  {/* Stats Grid */}
                  <div className="grid md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-xl p-5 shadow-sm">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Clicks</p>
                      <p className="text-2xl font-bold text-[#003456] mt-1">{dashboard?.stats?.total_clicks || 0}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Conversions</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">{dashboard?.stats?.total_conversions || 0}</p>
                      <p className="text-xs text-gray-400">{dashboard?.stats?.conversion_rate || 0}% rate</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Sales</p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">${(dashboard?.stats?.total_sales || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Earned</p>
                      <p className="text-2xl font-bold text-purple-600 mt-1">${(dashboard?.stats?.total_commission || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Tier Progress Card - Only show if tierInfo available */}
                  {tierInfo && (
                    <div className="bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-5 text-white mb-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getTierColor(tierInfo.current_tier?.name)}`}>
                            {tierInfo.current_tier?.name === 'Diamond' ? <Crown className="h-6 w-6" /> :
                             tierInfo.current_tier?.name === 'Platinum' ? <Medal className="h-6 w-6" /> :
                             tierInfo.current_tier?.name === 'Gold' ? <Trophy className="h-6 w-6" /> :
                             <Star className="h-6 w-6" />}
                          </div>
                          <div>
                            <p className="text-white/70 text-xs">Your Tier</p>
                            <p className="text-xl font-bold">{tierInfo.current_tier?.name || 'Bronze'}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-8">
                          <div className="text-center">
                            <p className="text-white/70 text-xs">Marketing Credits</p>
                            <p className="text-2xl font-bold text-emerald-400">{tierInfo.available_points}</p>
                          </div>
                          
                          {tierInfo.next_tier && !tierInfo.has_override && (
                            <div className="hidden md:block">
                              <p className="text-white/70 text-xs mb-1">Progress to {tierInfo.next_tier.name}</p>
                              <div className="w-32 h-2 bg-white/20 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-emerald-400 rounded-full"
                                  style={{ width: `${tierInfo.progress_to_next_tier}%` }}
                                />
                              </div>
                              <p className="text-[10px] text-white/50 mt-0.5">
                                ${tierInfo.total_sales?.toFixed(0)} / ${tierInfo.next_tier.min_sales?.toFixed(0)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Earnings & Period Stats */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Earnings Overview
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-gray-600">Total Commission</span>
                          <span className="font-bold text-green-600">${(dashboard?.stats?.total_commission || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">Commission in Lieu</span>
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">30-day hold</span>
                          </div>
                          <span className="font-bold text-amber-600">${(dashboard?.stats?.commission_in_lieu || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-gray-600">Pending Payout</span>
                          <span className="font-bold text-orange-600">${(dashboard?.stats?.pending_payout || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Already Paid</span>
                          <span className="font-bold text-blue-600">${(dashboard?.stats?.paid_commission || 0).toFixed(2)}</span>
                        </div>
                      </div>
                      {(dashboard?.stats?.commission_in_lieu || 0) > 0 && (
                        <p className="text-xs text-gray-500 mt-3 italic">
                          Commission in Lieu is held for 30 days from the order date before becoming available for payout.
                        </p>
                      )}
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        This Month
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-gray-600">Monthly Sales</span>
                          <span className="font-medium">${(dashboard?.period_stats?.monthly_sales || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b">
                          <span className="text-gray-600">Monthly Commission</span>
                          <span className="font-medium text-green-600">${(dashboard?.period_stats?.monthly_commission || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="text-gray-600">Active Visitors</span>
                          <span className="font-medium">{dashboard?.pending_visitors || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Stats */}
                  {dashboard?.location_stats && (dashboard?.location_stats?.by_country?.length > 0 || dashboard?.location_stats?.by_city?.length > 0) && (
                    <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
                      <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <span className="text-lg">📍</span>
                        Customer Locations
                      </h4>
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* By Country */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-500 mb-3">By Country</h5>
                          {dashboard.location_stats.by_country?.length > 0 ? (
                            <div className="space-y-2">
                              {dashboard.location_stats.by_country.map((loc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{loc.country === 'CA' ? '🇨🇦' : loc.country === 'US' ? '🇺🇸' : '🌍'}</span>
                                    <span className="font-medium">{loc.country === 'CA' ? 'Canada' : loc.country === 'US' ? 'United States' : loc.country}</span>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-bold text-[#003456]">{loc.orders} orders</p>
                                    <p className="text-xs text-gray-500">${loc.sales?.toFixed(2)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm">No country data yet</p>
                          )}
                        </div>
                        
                        {/* By City */}
                        <div>
                          <h5 className="text-sm font-medium text-gray-500 mb-3">Top Cities</h5>
                          {dashboard.location_stats.by_city?.length > 0 ? (
                            <div className="space-y-2">
                              {dashboard.location_stats.by_city.slice(0, 5).map((loc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                  <span className="font-medium text-sm">{loc.city}</span>
                                  <div className="text-right">
                                    <p className="font-bold text-[#003456]">{loc.orders}</p>
                                    <p className="text-xs text-gray-500">${loc.sales?.toFixed(2)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm">No city data yet</p>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-4">
                        Showing customers from {dashboard.location_stats.total_countries || 0} countries and {dashboard.location_stats.total_cities || 0} cities
                      </p>
                    </div>
                  )}

                  {/* Recent Sales */}
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <h4 className="font-semibold text-gray-700 mb-4">Recent Sales</h4>
                    {dashboard?.recent_sales?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-3 font-medium text-gray-600">Date</th>
                              <th className="text-left p-3 font-medium text-gray-600">Order</th>
                              <th className="text-left p-3 font-medium text-gray-600">Location</th>
                              <th className="text-left p-3 font-medium text-gray-600">Amount</th>
                              <th className="text-left p-3 font-medium text-gray-600">Commission</th>
                              <th className="text-left p-3 font-medium text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dashboard.recent_sales.map((sale, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-3">{new Date(sale.converted_at).toLocaleDateString()}</td>
                                <td className="p-3 font-mono text-xs">{sale.order_id?.slice(0, 8)}...</td>
                                <td className="p-3">
                                  {sale.customer_city || sale.customer_country ? (
                                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                      {sale.customer_city ? `${sale.customer_city}, ` : ''}{sale.customer_country || ''}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400 text-xs">—</span>
                                  )}
                                </td>
                                <td className="p-3">${sale.order_total?.toFixed(2)}</td>
                                <td className="p-3 text-green-600">${sale.commission_amount?.toFixed(2)}</td>
                                <td className="p-3">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    sale.status === 'paid' ? 'bg-green-100 text-green-700' :
                                    sale.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {sale.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No sales yet. Start sharing your affiliate link!</p>
                    )}
                  </div>
                </TabsContent>

                {/* Short Links Tab */}
                <TabsContent value="short-links">
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                          <LinkIcon className="h-5 w-5" />
                          Short Links
                        </h4>
                        <p className="text-sm text-gray-500">Create memorable, short links for your campaigns</p>
                      </div>
                      <Button onClick={() => setShowShortLinkBuilder(true)} className="bg-[#003456]" data-testid="create-short-link-btn">
                        <Plus className="h-4 w-4 mr-1" /> Create Short Link
                      </Button>
                    </div>
                    
                    {shortLinks.length > 0 ? (
                      <div className="space-y-3">
                        {shortLinks.map(link => (
                          <div key={link.code} className="border rounded-xl p-4 hover:shadow-sm transition-shadow">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <code className="bg-[#003456] text-white px-3 py-1 rounded-lg text-sm font-medium">
                                    /r/{link.code}
                                  </code>
                                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/r/${link.code}`)}>
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2 truncate">→ {link.destination_url}</p>
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-lg font-bold text-[#003456]">{link.clicks || 0}</p>
                                  <p className="text-xs text-gray-500">clicks</p>
                                </div>
                                <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteShortLink(link.code)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <LinkIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No short links yet</p>
                        <p className="text-sm text-gray-400 mt-1">Create custom short links for easier sharing</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Campaigns Tab - New Campaign System with Point Distribution */}
                <TabsContent value="campaigns">
                  <div className="space-y-6">
                    {/* Tier Info Card */}
                    {tierInfo && (
                      <div className="bg-gradient-to-r from-[#003456] to-[#001d30] rounded-2xl p-6 text-white">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getTierColor(tierInfo.current_tier?.name)}`}>
                              {tierInfo.current_tier?.name === 'Diamond' ? <Crown className="h-8 w-8" /> :
                               tierInfo.current_tier?.name === 'Platinum' ? <Medal className="h-8 w-8" /> :
                               tierInfo.current_tier?.name === 'Gold' ? <Trophy className="h-8 w-8" /> :
                               <Star className="h-8 w-8" />}
                            </div>
                            <div>
                              <p className="text-white/70 text-sm">Your Tier</p>
                              <p className="text-2xl font-bold">{tierInfo.current_tier?.name || 'Bronze'}</p>
                              {tierInfo.has_override && (
                                <span className="text-xs bg-green-500/20 text-green-300 px-2 py-0.5 rounded">Custom Allocation</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-center md:text-right">
                            <p className="text-white/70 text-sm">Marketing Credits Available</p>
                            <p className="text-4xl font-bold text-emerald-400">{tierInfo.available_points}</p>
                            <p className="text-xs text-white/60">Credits to distribute per campaign</p>
                          </div>
                        </div>
                        
                        {/* Progress to Next Tier */}
                        {tierInfo.next_tier && !tierInfo.has_override && (
                          <div className="mt-6">
                            <div className="flex justify-between text-sm mb-2">
                              <span>Progress to {tierInfo.next_tier.name} Tier</span>
                              <span>${tierInfo.total_sales?.toFixed(0)} / ${tierInfo.next_tier.min_sales?.toFixed(0)}</span>
                            </div>
                            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-emerald-400 rounded-full transition-all"
                                style={{ width: `${tierInfo.progress_to_next_tier}%` }}
                              />
                            </div>
                            <p className="text-xs text-white/60 mt-1">
                              🚀 +{tierInfo.next_tier.available_points - tierInfo.available_points} more credits at next tier!
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Campaign Builder */}
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                            <PieChart className="h-5 w-5" />
                            My Campaigns
                          </h4>
                          <p className="text-sm text-gray-500">Distribute your marketing credits between your earnings and customer savings</p>
                        </div>
                        <Button onClick={() => setShowCampaignBuilder(true)} data-testid="create-campaign-btn">
                          <Plus className="h-4 w-4 mr-1" /> New Campaign
                        </Button>
                      </div>
                      
                      {/* Campaign List */}
                      {campaigns.length > 0 ? (
                        <div className="space-y-4">
                          {campaigns.map((campaign) => (
                            <div 
                              key={campaign.id} 
                              className={`border rounded-xl p-4 transition-all ${campaign.is_active ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`}
                            >
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h5 className="font-semibold text-gray-800">{campaign.campaign_name}</h5>
                                    <span className={`text-xs px-2 py-0.5 rounded ${campaign.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                                      {campaign.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-1 mb-2">
                                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{campaign.campaign_code}</code>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(campaign.campaign_code)}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="text-emerald-600 font-medium flex items-center gap-1">
                                      💰 {campaign.commission_percent}% Your Earnings
                                    </span>
                                    <span className="text-blue-600 font-medium flex items-center gap-1">
                                      🎁 {campaign.discount_percent}% Customer Savings
                                    </span>
                                    <span className="text-gray-500">
                                      ({campaign.total_points_used} credits used)
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-[#003456]">${campaign.total_sales?.toFixed(2) || '0.00'}</p>
                                    <p className="text-xs text-gray-500">{campaign.total_orders || 0} orders</p>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      onClick={() => fetchCampaignCustomers(campaign.id)}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant={campaign.is_active ? "outline" : "default"}
                                      size="sm"
                                      onClick={() => handleToggleCampaign(campaign.id, campaign.is_active)}
                                    >
                                      {campaign.is_active ? 'Pause' : 'Activate'}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                              {/* Show locations if available */}
                              {campaign.unique_locations && campaign.unique_locations.length > 0 && (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="text-xs text-gray-500">📍 Customers from:</span>
                                  <div className="flex gap-1 flex-wrap">
                                    {campaign.unique_locations.map((loc, idx) => (
                                      <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                        {loc}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {campaign.description && (
                                <p className="text-sm text-gray-500 mt-2">{campaign.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <PieChart className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-600 mb-2">No Campaigns Yet</h3>
                          <p className="text-gray-500 mb-4">Create your first campaign to start tracking performance</p>
                          <Button onClick={() => setShowCampaignBuilder(true)}>
                            <Plus className="h-4 w-4 mr-1" /> Create Your First Campaign
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Tier Levels Reference */}
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <h4 className="font-semibold text-gray-700 mb-2">Marketing Credit Tiers</h4>
                      <p className="text-sm text-gray-500 mb-4">Earn more marketing credits as your sales grow. Higher tier = more flexibility!</p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {tierInfo?.all_tiers?.map((tier) => (
                          <div 
                            key={tier.tier}
                            className={`rounded-xl p-4 text-center border-2 transition-all ${
                              tierInfo.current_tier?.tier === tier.tier 
                                ? 'border-[#003456] bg-[#003456]/5' 
                                : 'border-gray-100'
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${getTierColor(tier.name)}`}>
                              {tier.name === 'Diamond' ? <Crown className="h-5 w-5" /> :
                               tier.name === 'Platinum' ? <Medal className="h-5 w-5" /> :
                               tier.name === 'Gold' ? <Trophy className="h-5 w-5" /> :
                               <Star className="h-5 w-5" />}
                            </div>
                            <p className="font-semibold text-sm">{tier.name}</p>
                            <p className="text-2xl font-bold text-[#003456]">{tier.available_points}</p>
                            <p className="text-xs text-gray-500">credits</p>
                            <p className="text-xs text-gray-400 mt-1">${tier.min_sales.toLocaleString()}+ sales</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Leaderboard Tab */}
                <TabsContent value="leaderboard">
                  <div className="grid md:grid-cols-3 gap-6">
                    {/* Your Stats */}
                    <div className="bg-gradient-to-br from-[#003456] to-[#001d30] rounded-2xl p-6 text-white">
                      <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Star className="h-5 w-5" /> Your Stats
                      </h4>
                      {leaderboardStats && (
                        <div className="space-y-4">
                          <div>
                            <p className="text-white/70 text-sm">Your Rank</p>
                            <p className="text-3xl font-bold">#{leaderboardStats.your_rank || '—'}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-white/70 text-sm">Clicks</p>
                              <p className="text-xl font-bold">{leaderboardStats.total_clicks || 0}</p>
                            </div>
                            <div>
                              <p className="text-white/70 text-sm">Conversions</p>
                              <p className="text-xl font-bold">{leaderboardStats.total_conversions || 0}</p>
                            </div>
                          </div>
                          <div className="pt-4 border-t border-white/20">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="checkbox" 
                                checked={showOnLeaderboard}
                                onChange={toggleLeaderboardVisibility}
                                className="rounded"
                              />
                              <span className="text-sm">Show me on leaderboard</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Leaderboard */}
                    <div className="md:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                      <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Trophy className="h-5 w-5 text-yellow-500" /> Top Affiliates
                      </h4>
                      {leaderboard.length > 0 ? (
                        <div className="space-y-2">
                          {leaderboard.map((entry, idx) => (
                            <div 
                              key={idx} 
                              className={`flex items-center gap-4 p-3 rounded-xl transition-colors ${
                                entry.is_you ? 'bg-yellow-50 border border-yellow-200' : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="w-10 h-10 flex items-center justify-center">
                                {entry.rank === 1 ? <Crown className="h-6 w-6 text-yellow-500" /> :
                                 entry.rank === 2 ? <Medal className="h-6 w-6 text-gray-400" /> :
                                 entry.rank === 3 ? <Medal className="h-6 w-6 text-amber-600" /> :
                                 <span className="text-lg font-bold text-gray-400">#{entry.rank}</span>}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{entry.name} {entry.is_you && <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full ml-2">You</span>}</p>
                                <p className="text-xs text-gray-500">{entry.total_clicks} clicks • {entry.total_conversions} conversions</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No affiliates on the leaderboard yet</p>
                          <p className="text-sm text-gray-400 mt-1">Be the first to opt in!</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Media Library Tab */}
                <TabsContent value="media">
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                          <Image className="h-5 w-5" /> Media Library
                        </h4>
                        <p className="text-sm text-gray-500">Marketing assets for your campaigns</p>
                      </div>
                      <Button variant="outline" onClick={() => setShowMediaRequestForm(true)}>
                        <Send className="h-4 w-4 mr-1" /> Request Asset
                      </Button>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      <button
                        onClick={() => { setSelectedMediaCategory(''); fetchMediaLibrary(''); }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          !selectedMediaCategory ? 'bg-[#003456] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      {mediaCategories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => { setSelectedMediaCategory(cat.id); fetchMediaLibrary(cat.id); }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                            selectedMediaCategory === cat.id ? 'bg-[#003456] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {cat.id === 'logos' && <Image className="h-4 w-4" />}
                          {cat.id === 'videos' && <Video className="h-4 w-4" />}
                          {cat.id === 'banners' && <Folder className="h-4 w-4" />}
                          {cat.name}
                        </button>
                      ))}
                    </div>

                    {/* Assets Grid */}
                    {mediaAssets.length > 0 ? (
                      <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {mediaAssets.map(asset => (
                          <div key={asset.id} className="border rounded-xl overflow-hidden hover:shadow-md transition-shadow group">
                            {asset.file_type === 'image' && (
                              <div className="aspect-video bg-gray-100 relative">
                                <img src={asset.file_url} alt={asset.name} className="w-full h-full object-cover" />
                              </div>
                            )}
                            {asset.file_type === 'video' && (
                              <div className="aspect-video bg-gray-900 flex items-center justify-center">
                                <Video className="h-10 w-10 text-white/50" />
                              </div>
                            )}
                            {asset.file_type === 'pdf' && (
                              <div className="aspect-video bg-red-50 flex items-center justify-center">
                                <FileText className="h-10 w-10 text-red-500" />
                              </div>
                            )}
                            <div className="p-3">
                              <p className="font-medium text-sm truncate">{asset.name}</p>
                              <p className="text-xs text-gray-500 truncate">{asset.description || asset.category}</p>
                              <a 
                                href={asset.file_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-xs text-[#003456] font-medium hover:underline"
                              >
                                <Download className="h-3 w-3" /> Download
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Folder className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No assets in this category</p>
                        <p className="text-sm text-gray-400 mt-1">Check back later or request specific assets</p>
                      </div>
                    )}

                    {/* My Requests */}
                    {mediaRequests.length > 0 && (
                      <div className="mt-8 pt-6 border-t">
                        <h5 className="font-medium text-gray-700 mb-4">Your Requests</h5>
                        <div className="space-y-2">
                          {mediaRequests.map(req => (
                            <div key={req.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div>
                                <p className="text-sm font-medium">{req.description.substring(0, 50)}...</p>
                                <p className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString()}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                req.status === 'completed' ? 'bg-green-100 text-green-700' :
                                req.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                req.status === 'declined' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {req.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Tracking Links Tab */}
                <TabsContent value="links">
                  <div className="bg-white rounded-2xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        <Link2 className="h-5 w-5" />
                        Custom Tracking Links
                      </h4>
                      <Button onClick={() => setShowLinkBuilder(true)}>
                        <Plus className="h-4 w-4 mr-1" /> New Link
                      </Button>
                    </div>
                    
                    {trackingLinks.length > 0 ? (
                      <div className="space-y-4">
                        {trackingLinks.map((link, idx) => (
                          <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex items-start justify-between">
                              <div>
                                <h5 className="font-medium text-gray-800">{link.campaign_name}</h5>
                                <p className="text-sm text-gray-500 mt-1">
                                  {link.source} / {link.medium}
                                  {link.term && ` / ${link.term}`}
                                </p>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(link.tracking_url)}>
                                <Copy className="h-4 w-4" />
                              </Button>
                            </div>
                            <code className="text-xs bg-gray-100 px-2 py-1 rounded mt-2 block truncate">
                              {link.tracking_url}
                            </code>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Link2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-600 mb-2">No Tracking Links Yet</h3>
                        <p className="text-gray-500 mb-4">Create custom links to track different marketing campaigns</p>
                        <Button onClick={() => setShowLinkBuilder(true)}>
                          <Plus className="h-4 w-4 mr-1" /> Create Your First Link
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>

                {/* Legal Tab */}
                <TabsContent value="legal">
                  {/* Agreement Timestamps */}
                  {(dashboard?.affiliate?.terms_accepted_at || dashboard?.affiliate?.disclosure_accepted_at) && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6" data-testid="agreement-timestamps">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <h4 className="font-semibold text-green-800">Program Agreements</h4>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        {dashboard?.affiliate?.terms_accepted_at && (
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Terms & Conditions Accepted</p>
                            <p className="text-sm font-medium text-gray-800 mt-1">
                              {new Date(dashboard.affiliate.terms_accepted_at).toLocaleString('en-US', {
                                dateStyle: 'medium',
                                timeStyle: 'short'
                              })}
                            </p>
                          </div>
                        )}
                        {dashboard?.affiliate?.disclosure_accepted_at && (
                          <div className="bg-white rounded-lg p-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Disclosure Requirements Accepted</p>
                            <p className="text-sm font-medium text-gray-800 mt-1">
                              {new Date(dashboard.affiliate.disclosure_accepted_at).toLocaleString('en-US', {
                                dateStyle: 'medium',
                                timeStyle: 'short'
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-green-700 mt-3">
                        These timestamps are recorded for compliance and legal purposes.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <FileText className="h-6 w-6 text-[#003456]" />
                        <h3 className="text-lg font-semibold">Affiliate Terms & Conditions</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        The legal agreement you accepted when joining the program.
                      </p>
                      <Button variant="outline" onClick={() => fetchLegalDoc('terms')}>
                        <Eye className="h-4 w-4 mr-1" /> View Terms
                      </Button>
                    </div>
                    
                    <div className="bg-white rounded-2xl shadow-sm p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <Shield className="h-6 w-6 text-purple-600" />
                        <h3 className="text-lg font-semibold">Disclosure Requirements (US & Canada)</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Required disclosure guidelines for the United States (FTC) and Canada (Competition Bureau).
                      </p>
                      <Button variant="outline" onClick={() => fetchLegalDoc('disclosure')}>
                        <Eye className="h-4 w-4 mr-1" /> View Requirements
                      </Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          ) : applicationSuccess ? (
            /* Application Success */
            <div className="max-w-xl mx-auto text-center">
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <Mail className="h-16 w-16 text-[#003456] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#0a0203] mb-2">Application Submitted!</h2>
                <p className="text-gray-600 mb-6">
                  Check your email for a link to set up your password and access your affiliate dashboard.
                </p>
                <div className="bg-blue-50 rounded-lg p-4 text-left">
                  <p className="text-sm text-blue-800">
                    <strong>Next Steps:</strong>
                  </p>
                  <ol className="text-sm text-blue-700 mt-2 list-decimal pl-4 space-y-1">
                    <li>Check your email inbox (and spam folder)</li>
                    <li>Click the password setup link</li>
                    <li>Create your password</li>
                    <li>Log in to your dashboard</li>
                  </ol>
                </div>
              </div>
            </div>
          ) : (
            /* Login / Sign Up View */
            <>
              {/* Benefits */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="h-7 w-7 text-emerald-600" />
                  </div>
                  <p className="text-xl font-bold text-[#003456]">Up to 15% Earnings</p>
                  <p className="text-gray-500">Customize your commission per campaign</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Gift className="h-7 w-7 text-blue-600" />
                  </div>
                  <p className="text-xl font-bold text-[#003456]">Customer Savings</p>
                  <p className="text-gray-500">Offer discounts to increase conversions</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="h-7 w-7 text-purple-600" />
                  </div>
                  <p className="text-xl font-bold text-[#003456]">Tier Rewards</p>
                  <p className="text-gray-500">Earn more credits as you grow</p>
                </div>
              </div>

              {/* How It Works Section */}
              <div className="bg-gradient-to-r from-[#003456]/5 to-blue-50 rounded-2xl p-8 mb-12">
                <h3 className="text-xl font-bold text-[#003456] text-center mb-6">How Marketing Credits Work</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-[#003456] text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">1</div>
                    <p className="font-semibold text-gray-800">Start with 10 Credits</p>
                    <p className="text-sm text-gray-500">New affiliates get 10 marketing credits</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-[#003456] text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">2</div>
                    <p className="font-semibold text-gray-800">Create Campaigns</p>
                    <p className="text-sm text-gray-500">Split credits between your earnings & customer savings</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-[#003456] text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">3</div>
                    <p className="font-semibold text-gray-800">Share Your Code</p>
                    <p className="text-sm text-gray-500">Customers use your campaign code at checkout</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-[#003456] text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">4</div>
                    <p className="font-semibold text-gray-800">Earn Up to 15 Credits</p>
                    <p className="text-sm text-gray-500">Unlock more credits as your sales grow</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Login Form */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6 flex items-center gap-2">
                    <Lock className="h-6 w-6" />
                    Affiliate Login
                  </h2>
                  
                  {needsPasswordSetup ? (
                    <div className="text-center py-4">
                      <Mail className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                      <p className="text-gray-600 mb-4">You need to set up your password first.</p>
                      <Button onClick={handleResendPasswordLink}>
                        Send Password Setup Link
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input
                          type="email"
                          required
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <Input
                          type="password"
                          required
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          placeholder="Your password"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm text-[#003456] hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Forgot Password?
                        </button>
                      </div>
                      <Button type="submit" className="w-full bg-[#003456]" disabled={submitting}>
                        {submitting ? 'Logging in...' : 'Login to Dashboard'}
                      </Button>
                    </form>
                  )}
                </div>
                
                {/* Application Form */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Apply Now</h2>
                  
                  <form onSubmit={handleSubmitApplication} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <Input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company (optional)</label>
                      <Input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        placeholder="Your company or brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                      <Input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>

                    {/* Legal Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Required Legal Documents</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Before your application can be submitted, you'll need to read and accept our Terms & Conditions and Disclosure Requirements.
                          </p>
                        </div>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-[#003456] hover:bg-[#001d30]" 
                      disabled={submitting}
                      data-testid="apply-now-btn"
                    >
                      {submitting ? 'Submitting...' : 'Review Documents & Apply'}
                    </Button>
                  </form>
                </div>
              </div>

              {/* Legal Documents Section - Below Login/Application Forms */}
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 mt-12 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-[#003456]" />
                  <h3 className="text-lg font-semibold text-[#0a0203]">Important Legal Documents</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Please review these documents before applying to our affiliate program:
                </p>
                <div className="flex flex-wrap gap-4">
                  <button
                    onClick={() => fetchLegalDoc('terms')}
                    className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-[#003456] hover:shadow-sm transition-all text-sm font-medium text-[#003456]"
                    data-testid="legal-terms-button"
                  >
                    <FileText className="h-4 w-4" />
                    Affiliate Terms & Conditions
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </button>
                  <button
                    onClick={() => fetchLegalDoc('disclosure')}
                    className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-[#003456] hover:shadow-sm transition-all text-sm font-medium text-[#003456]"
                    data-testid="legal-disclosure-button"
                  >
                    <Shield className="h-4 w-4" />
                    Disclosure Requirements (US & Canada)
                    <ExternalLink className="h-3 w-3 opacity-50" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Link Builder Dialog */}
      <Dialog open={showLinkBuilder} onOpenChange={setShowLinkBuilder}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Create Tracking Link
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTrackingLink} className="space-y-4 pt-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Campaign Name *</label>
              <Input
                required
                value={newLink.campaign_name}
                onChange={(e) => setNewLink({ ...newLink, campaign_name: e.target.value })}
                placeholder="e.g., Summer Sale 2024"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Source *</label>
                <Input
                  required
                  value={newLink.source}
                  onChange={(e) => setNewLink({ ...newLink, source: e.target.value })}
                  placeholder="e.g., youtube, facebook"
                />
                <p className="text-xs text-gray-400 mt-1">Where the traffic comes from</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Medium *</label>
                <Input
                  required
                  value={newLink.medium}
                  onChange={(e) => setNewLink({ ...newLink, medium: e.target.value })}
                  placeholder="e.g., video, post, email"
                />
                <p className="text-xs text-gray-400 mt-1">Type of marketing</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Term (optional)</label>
                <Input
                  value={newLink.term}
                  onChange={(e) => setNewLink({ ...newLink, term: e.target.value })}
                  placeholder="e.g., freeze drying"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Content (optional)</label>
                <Input
                  value={newLink.content}
                  onChange={(e) => setNewLink({ ...newLink, content: e.target.value })}
                  placeholder="e.g., banner-1, link-bio"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowLinkBuilder(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]">Create Link</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Legal Document Dialog */}
      <Dialog open={!!showLegalDialog} onOpenChange={() => setShowLegalDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{legalContent?.title}</DialogTitle>
            {legalContent?.subtitle && (
              <p className="text-sm text-gray-500 font-medium">{legalContent.subtitle}</p>
            )}
            {legalContent?.effective_date && (
              <p className="text-xs text-gray-400">Effective Date: {legalContent.effective_date}</p>
            )}
          </DialogHeader>
          <div className="space-y-6 pt-4">
            {/* Overview or Introduction */}
            {(legalContent?.overview || legalContent?.introduction) && (
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-gray-700 text-sm leading-relaxed">{legalContent.overview || legalContent.introduction}</p>
              </div>
            )}
            
            {/* Sections - handles both disclosure and terms formats */}
            {legalContent?.sections?.map((section, idx) => (
              <div key={idx} className="border border-gray-200 rounded-xl p-5">
                <h4 className="font-bold text-[#003456] mb-3 text-lg">
                  {section.number ? `${section.number}. ` : ''}{section.title}
                </h4>
                <p className="text-sm text-gray-700 mb-3">{section.content}</p>
                
                {/* Points list (for terms) */}
                {section.points && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.points.map((point, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Requirements list (for disclosure) */}
                {section.requirements && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Placements list */}
                {section.placements && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.placements.map((place, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{place}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Acceptable hashtags */}
                {section.acceptable_hashtags && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {section.acceptable_hashtags.map((tag, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Prohibited activities (for terms) */}
                {section.prohibited && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.prohibited.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-600">
                        <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Penalties list */}
                {section.penalties && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.penalties.map((penalty, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-red-600">
                        <X className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{penalty}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Responsibilities list */}
                {section.responsibilities && (
                  <ul className="space-y-2 ml-4 mt-3">
                    {section.responsibilities.map((resp, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{resp}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Subsections (for terms like Marketing Credits) */}
                {section.subsections && (
                  <div className="space-y-3 mt-4 pl-4 border-l-2 border-[#003456]/20">
                    {section.subsections.map((sub, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3">
                        <h5 className="font-semibold text-[#003456] mb-1">{sub.subtitle}</h5>
                        <p className="text-sm text-gray-600">{sub.description}</p>
                        {sub.link && (
                          <a href={sub.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#003456] hover:underline flex items-center gap-1 mt-1">
                            <ExternalLink className="h-3 w-3" /> View Guidance
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Example (for terms like Commission Holding Period) */}
                {section.example && (
                  <div className="bg-blue-50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-blue-700"><strong>Example:</strong> {section.example}</p>
                  </div>
                )}
                
                {/* Note */}
                {section.note && (
                  <p className="text-sm text-gray-500 mt-3 italic">{section.note}</p>
                )}
                
                {/* Warning */}
                {section.warning && (
                  <p className="text-sm text-amber-600 mt-3 font-medium bg-amber-50 p-2 rounded">
                    ⚠️ {section.warning}
                  </p>
                )}
              </div>
            ))}

            {/* Old requirements format (for terms) */}
            {legalContent?.requirements && !legalContent?.sections && legalContent.requirements.map((req, idx) => (
              <div key={idx} className="border-b pb-4 last:border-0">
                <h4 className="font-semibold text-gray-800 mb-2">{req.title}</h4>
                <p className="text-sm text-gray-600">{req.content}</p>
              </div>
            ))}

            {/* Regulatory Resources */}
            {legalContent?.regulatory_resources && (
              <div className="bg-blue-50 rounded-xl p-5">
                <h4 className="font-bold text-[#003456] mb-4">Official Regulatory Resources</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* United States */}
                  {legalContent.regulatory_resources.united_states && (
                    <div className="bg-white rounded-lg p-4">
                      <h5 className="font-semibold text-gray-800 mb-2">
                        🇺🇸 {legalContent.regulatory_resources.united_states.agency}
                      </h5>
                      <ul className="space-y-2">
                        {legalContent.regulatory_resources.united_states.links.map((link, i) => (
                          <li key={i}>
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-[#003456] hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {link.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* Canada */}
                  {legalContent.regulatory_resources.canada && (
                    <div className="bg-white rounded-lg p-4">
                      <h5 className="font-semibold text-gray-800 mb-2">
                        🇨🇦 {legalContent.regulatory_resources.canada.agency}
                      </h5>
                      <ul className="space-y-2">
                        {legalContent.regulatory_resources.canada.links.map((link, i) => (
                          <li key={i}>
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-[#003456] hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {link.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Sample Disclosures */}
            {legalContent?.sample_disclosures && (
              <div className="bg-green-50 rounded-xl p-5">
                <h4 className="font-bold text-[#003456] mb-4">Sample Disclosure Statements</h4>
                <div className="space-y-3">
                  {Object.entries(legalContent.sample_disclosures).map(([key, value]) => (
                    <div key={key} className="bg-white rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500 uppercase mb-1">{key.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-700 italic">"{value}"</p>
                      <button 
                        onClick={() => copyToClipboard(value)}
                        className="text-xs text-[#003456] hover:underline mt-1"
                      >
                        Copy to clipboard
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compliance Statement */}
            {legalContent?.compliance_statement && (
              <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-[#003456]">
                <p className="text-sm text-gray-700 font-medium">{legalContent.compliance_statement}</p>
              </div>
            )}

            <p className="text-xs text-gray-400 text-right">Last updated: {legalContent?.last_updated}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Short Link Builder Dialog */}
      <Dialog open={showShortLinkBuilder} onOpenChange={setShowShortLinkBuilder}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Create Short Link
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateShortLink} className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custom Slug (optional)</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm">{window.location.origin}/r/</span>
                <Input
                  value={newShortLink.custom_slug}
                  onChange={(e) => setNewShortLink({ ...newShortLink, custom_slug: e.target.value })}
                  placeholder="my-promo"
                  className="flex-1"
                  data-testid="short-link-slug-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Leave empty for auto-generated code. Only letters, numbers, and hyphens.</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination Page</label>
              <select
                value={newShortLink.destination_url}
                onChange={(e) => setNewShortLink({ ...newShortLink, destination_url: e.target.value })}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="/">Homepage</option>
                <option value="/products">Products Page</option>
                <option value="/shop">Shop</option>
                <option value="/blog">Blog</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UTM Source</label>
                <Input
                  value={newShortLink.utm_source}
                  onChange={(e) => setNewShortLink({ ...newShortLink, utm_source: e.target.value })}
                  placeholder="instagram"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UTM Medium</label>
                <Input
                  value={newShortLink.utm_medium}
                  onChange={(e) => setNewShortLink({ ...newShortLink, utm_medium: e.target.value })}
                  placeholder="bio_link"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UTM Campaign</label>
              <Input
                value={newShortLink.utm_campaign}
                onChange={(e) => setNewShortLink({ ...newShortLink, utm_campaign: e.target.value })}
                placeholder="summer_sale_2024"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowShortLinkBuilder(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]" disabled={creatingShortLink} data-testid="create-short-link-submit">
                {creatingShortLink ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Creating...</> : 'Create Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Request Dialog */}
      <Dialog open={showMediaRequestForm} onOpenChange={setShowMediaRequestForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Request Marketing Asset
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitMediaRequest} className="space-y-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Request Type</label>
              <select
                value={newMediaRequest.request_type}
                onChange={(e) => setNewMediaRequest({ ...newMediaRequest, request_type: e.target.value })}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="specific_asset">Specific Asset (logo, image, etc.)</option>
                <option value="category">Entire Category</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Describe what you need</label>
              <textarea
                value={newMediaRequest.description}
                onChange={(e) => setNewMediaRequest({ ...newMediaRequest, description: e.target.value })}
                className="w-full p-3 border rounded-lg text-sm h-24"
                placeholder="e.g., High-resolution logo for Instagram profile, product photos with white background..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Urgency</label>
              <div className="flex gap-2">
                {['low', 'normal', 'high'].map(level => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setNewMediaRequest({ ...newMediaRequest, urgency: level })}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm capitalize transition-colors ${
                      newMediaRequest.urgency === level 
                        ? level === 'high' ? 'bg-red-100 text-red-700 border-2 border-red-300' 
                        : level === 'normal' ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                        : 'bg-gray-100 text-gray-700 border-2 border-gray-300'
                        : 'bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowMediaRequestForm(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#003456]">
                <Send className="h-4 w-4 mr-1" /> Submit Request
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog - Multi-step verification */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { if (!open) resetForgotPasswordState(); else setShowForgotPassword(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Reset Your Password
            </DialogTitle>
          </DialogHeader>
          
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 py-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${forgotPasswordStep >= 1 ? 'bg-[#003456] text-white' : 'bg-gray-200 text-gray-500'}`}>1</div>
            <div className={`w-12 h-1 ${forgotPasswordStep >= 2 ? 'bg-[#003456]' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${forgotPasswordStep >= 2 ? 'bg-[#003456] text-white' : 'bg-gray-200 text-gray-500'}`}>2</div>
            <div className={`w-12 h-1 ${forgotPasswordStep >= 3 ? 'bg-[#003456]' : 'bg-gray-200'}`} />
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${forgotPasswordStep >= 3 ? 'bg-[#003456] text-white' : 'bg-gray-200 text-gray-500'}`}>3</div>
          </div>
          
          {/* Step 1: Enter Email */}
          {forgotPasswordStep === 1 && (
            <form onSubmit={handleForgotPassword} className="space-y-4 pt-4">
              <p className="text-sm text-gray-600">
                Enter your email address and we'll send you a 6-digit verification code.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <Input
                  type="email"
                  required
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="forgot-password-email"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={resetForgotPasswordState}>Cancel</Button>
                <Button type="submit" className="bg-[#003456]" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Code'}
                </Button>
              </div>
            </form>
          )}
          
          {/* Step 2: Enter Verification Code */}
          {forgotPasswordStep === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4 pt-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-sm text-gray-600">
                  We sent a verification code to <strong>{forgotPasswordEmail}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 text-center">Enter 6-Digit Code</label>
                <Input
                  type="text"
                  required
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="verification-code-input"
                />
                <p className="text-xs text-gray-500 text-center mt-2">Code expires in 15 minutes</p>
              </div>
              <div className="flex justify-between items-center pt-2">
                <button 
                  type="button" 
                  onClick={() => setForgotPasswordStep(1)}
                  className="text-sm text-[#003456] hover:underline"
                >
                  ← Change email
                </button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={resetForgotPasswordState}>Cancel</Button>
                  <Button type="submit" className="bg-[#003456]" disabled={submitting || verificationCode.length !== 6}>
                    {submitting ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </div>
              </div>
              <div className="text-center pt-2">
                <button 
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={submitting}
                  className="text-sm text-gray-500 hover:text-[#003456]"
                >
                  Didn't receive code? Send again
                </button>
              </div>
            </form>
          )}
          
          {/* Step 3: Set New Password */}
          {forgotPasswordStep === 3 && (
            <form onSubmit={handleSetNewPassword} className="space-y-4 pt-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-sm text-gray-600">
                  Code verified! Now set your new password.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <Input
                  type="password"
                  required
                  minLength={6}
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  data-testid="confirm-password-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={resetForgotPasswordState}>Cancel</Button>
                <Button type="submit" className="bg-[#003456]" disabled={submitting}>
                  {submitting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog (from email link) */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Set New Password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 pt-4">
            <p className="text-sm text-gray-600">
              Enter your new password below.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="reset-new-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <Input
                type="password"
                required
                minLength={6}
                value={resetConfirmPassword}
                onChange={(e) => setResetConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                data-testid="reset-confirm-password"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowResetPassword(false);
                  setResetToken('');
                  searchParams.delete('reset_token');
                  setSearchParams(searchParams);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-[#003456]" disabled={resetPasswordLoading}>
                {resetPasswordLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Campaign Builder Dialog */}
      <Dialog open={showCampaignBuilder} onOpenChange={setShowCampaignBuilder}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <PieChart className="h-4 w-4" />
              Create New Campaign
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCampaign} className="space-y-3">
            {/* How It Works - Compact */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>💡 {tierInfo?.available_points || 10} credits</strong> to split between your earnings and customer savings. More customer discount = more conversions!
              </p>
              <p className="text-xs text-blue-600 mt-1">
                <strong>Note:</strong> Commissions are calculated on MSRP less marketing discount.
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-700">Campaign Name *</label>
              <Input
                required
                value={newCampaign.campaign_name}
                onChange={(e) => setNewCampaign({ ...newCampaign, campaign_name: e.target.value })}
                placeholder="e.g., Summer YouTube Campaign"
                className="h-9 text-sm"
                data-testid="campaign-name-input"
              />
            </div>
            
            {/* Custom Code Section */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={newCampaign.use_custom_code}
                  onChange={(e) => {
                    setNewCampaign({ ...newCampaign, use_custom_code: e.target.checked, custom_code: '' });
                    setCodeAvailability(null);
                  }}
                  className="rounded border-amber-400"
                />
                <span className="text-xs font-medium text-amber-800">Use custom campaign code</span>
              </label>
              
              {newCampaign.use_custom_code && (
                <div className="mt-2">
                  <div className="flex gap-2">
                    <Input
                      value={newCampaign.custom_code}
                      onChange={(e) => {
                        const code = e.target.value.toUpperCase().replace(/[^A-Z0-9\-_]/g, '');
                        setNewCampaign({ ...newCampaign, custom_code: code });
                        setCodeAvailability(null);
                      }}
                      onBlur={() => checkCodeAvailability(newCampaign.custom_code)}
                      placeholder="e.g., SUMMER2024"
                      maxLength={20}
                      className="h-8 text-sm font-mono uppercase"
                      data-testid="custom-code-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs"
                      onClick={() => checkCodeAvailability(newCampaign.custom_code)}
                      disabled={checkingCode || !newCampaign.custom_code || newCampaign.custom_code.length < 4}
                    >
                      {checkingCode ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Check'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-amber-700 mt-1">4-20 characters, letters, numbers, hyphens and underscores only</p>
                  {codeAvailability === true && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Code is available!
                    </p>
                  )}
                  {codeAvailability === false && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <X className="h-3 w-3" /> Code is not available
                    </p>
                  )}
                </div>
              )}
              
              {!newCampaign.use_custom_code && (
                <p className="text-[10px] text-amber-700">A unique code will be auto-generated for you</p>
              )}
            </div>
            
            {/* Point Distribution - Compact */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-center mb-3">
                <span className="text-xs font-medium text-gray-700">Distribute Credits</span>
                <span className="text-xs bg-[#003456] text-white px-2 py-0.5 rounded-full font-bold">
                  {tierInfo?.available_points || 10} available
                </span>
              </div>
              
              {/* Commission Slider - Compact */}
              <div className="bg-white rounded-lg p-3 border border-emerald-200 mb-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-emerald-700">💰 Your Earnings</label>
                  <span className="text-lg font-bold text-emerald-600">{newCampaign.commission_points}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={tierInfo?.available_points || 10}
                  step="0.5"
                  value={newCampaign.commission_points}
                  onChange={(e) => {
                    const commission = parseFloat(e.target.value);
                    const maxDiscount = (tierInfo?.available_points || 10) - commission;
                    setNewCampaign({ 
                      ...newCampaign, 
                      commission_points: commission,
                      discount_points: Math.min(newCampaign.discount_points, maxDiscount)
                    });
                  }}
                  className="w-full h-2 bg-emerald-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                  data-testid="commission-slider"
                />
              </div>
              
              {/* Customer Discount Slider - Compact */}
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-semibold text-blue-700">🎁 Customer Savings</label>
                  <span className="text-lg font-bold text-blue-600">{newCampaign.discount_points}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={(tierInfo?.available_points || 10) - newCampaign.commission_points}
                  step="0.5"
                  value={newCampaign.discount_points}
                  onChange={(e) => setNewCampaign({ ...newCampaign, discount_points: parseFloat(e.target.value) })}
                  className="w-full h-2 bg-blue-100 rounded-lg appearance-none cursor-pointer accent-blue-600"
                  data-testid="discount-slider"
                />
              </div>
              
              {/* Credits Summary - Compact */}
              <div className="mt-3 pt-2 border-t border-gray-200 flex justify-between items-center">
                <span className="text-xs text-gray-600">Used:</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        (newCampaign.commission_points + newCampaign.discount_points) > (tierInfo?.available_points || 10)
                          ? 'bg-red-500' 
                          : 'bg-gradient-to-r from-emerald-500 to-blue-500'
                      }`}
                      style={{ width: `${((newCampaign.commission_points + newCampaign.discount_points) / (tierInfo?.available_points || 10)) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-bold ${
                    (newCampaign.commission_points + newCampaign.discount_points) > (tierInfo?.available_points || 10)
                      ? 'text-red-600' 
                      : 'text-[#003456]'
                  }`}>
                    {newCampaign.commission_points + newCampaign.discount_points}/{tierInfo?.available_points || 10}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Optional fields in a row */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-gray-700">Description</label>
                <Input
                  value={newCampaign.description}
                  onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                  placeholder="Optional"
                  className="h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Target Audience</label>
                <Input
                  value={newCampaign.target_audience}
                  onChange={(e) => setNewCampaign({ ...newCampaign, target_audience: e.target.value })}
                  placeholder="Optional"
                  className="h-9 text-sm"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setShowCampaignBuilder(false)}>Cancel</Button>
              <Button 
                type="submit" 
                size="sm"
                className="bg-[#003456]" 
                disabled={creatingCampaign || (newCampaign.commission_points + newCampaign.discount_points) > (tierInfo?.available_points || 10)}
                data-testid="create-campaign-submit"
              >
                {creatingCampaign ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Campaign Customers Dialog - Anonymized View for Affiliates */}
      <Dialog open={!!selectedCampaign} onOpenChange={() => { setSelectedCampaign(null); setCampaignCustomers([]); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Campaign Performance: {selectedCampaign?.campaign_name}
            </DialogTitle>
          </DialogHeader>
          <div className="pt-4">
            {/* Campaign Stats */}
            {selectedCampaign && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-xl font-bold text-[#003456]">{selectedCampaign.total_orders || 0}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">Total Sales</p>
                  <p className="text-xl font-bold text-[#003456]">${selectedCampaign.total_sales?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-emerald-600">Your Earnings</p>
                  <p className="text-xl font-bold text-emerald-600">${selectedCampaign.total_commission?.toFixed(2) || '0.00'}</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-blue-600">Customer Savings</p>
                  <p className="text-xl font-bold text-blue-600">${selectedCampaign.total_discounts_given?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
            )}
            
            {/* Privacy Notice */}
            <div className="bg-gray-100 rounded-lg p-3 mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-gray-500" />
              <p className="text-xs text-gray-600">Customer privacy protected. Only location and order data shown.</p>
            </div>
            
            {/* Orders List - Anonymized */}
            {campaignCustomers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-gray-600">Location</th>
                      <th className="text-left p-3 font-medium text-gray-600">Order Total</th>
                      <th className="text-left p-3 font-medium text-gray-600">Your Earnings</th>
                      <th className="text-left p-3 font-medium text-gray-600">Customer Saved</th>
                      <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignCustomers.map((customer, idx) => (
                      <tr key={customer.id || idx} className="border-t hover:bg-gray-50">
                        <td className="p-3">
                          <p className="font-medium">{customer.location || 'Unknown'}</p>
                          <p className="text-xs text-gray-500">{customer.country || ''}</p>
                        </td>
                        <td className="p-3 font-medium">${customer.order_total?.toFixed(2)}</td>
                        <td className="p-3 text-emerald-600 font-medium">${customer.commission_amount?.toFixed(2)}</td>
                        <td className="p-3 text-blue-600">${customer.discount_amount?.toFixed(2)}</td>
                        <td className="p-3 text-gray-500">
                          {new Date(customer.purchased_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No orders yet for this campaign</p>
                <p className="text-sm text-gray-400 mt-1">Share your campaign code to start earning</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Opt-In Modal for Application - Read and Check Documents */}
      {showOptInModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-fade-in" data-testid="affiliate-opt-in-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-[#003456] text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">HomeLyph Affiliate Program</h2>
                <p className="text-white/80 text-sm">Required Documents – Please Read & Accept</p>
              </div>
              <button 
                onClick={() => setShowOptInModal(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingLegalDocs ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-[#003456]" />
                  <span className="ml-2 text-gray-600">Loading documents...</span>
                </div>
              ) : (
                <>
                  {/* Introduction */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-700 text-sm">
                      Before joining the HomeLyph Affiliate Program, you must read and accept both of the following legal documents. 
                      This ensures you understand your responsibilities as an affiliate and helps us maintain compliance with US (FTC) and Canadian (Competition Bureau) regulations.
                    </p>
                  </div>

                  {/* Document 1: Terms & Conditions */}
                  <div className="mb-4 border rounded-xl overflow-hidden">
                    <button 
                      onClick={() => setExpandedOptInSection(expandedOptInSection === 'terms' ? null : 'terms')}
                      className={`w-full flex items-center justify-between p-4 transition-colors ${
                        termsRead ? 'bg-green-50 hover:bg-green-100' : 'bg-blue-50 hover:bg-blue-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className={`h-6 w-6 ${termsRead ? 'text-green-600' : 'text-blue-600'}`} />
                        <div className="text-left">
                          <span className="text-lg font-bold text-gray-800">1. Affiliate Terms & Conditions</span>
                          <p className="text-sm text-gray-500">19 sections covering commission, payments, and program rules</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {termsRead && <CheckCircle className="h-5 w-5 text-green-600" />}
                        {expandedOptInSection === 'terms' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </button>
                    
                    {expandedOptInSection === 'terms' && termsContent && (
                      <div className="border-t max-h-[400px] overflow-y-auto p-4 bg-white">
                        <p className="text-xs text-gray-400 mb-3">Effective Date: {termsContent.effective_date}</p>
                        {termsContent.introduction && (
                          <p className="text-sm text-gray-600 mb-4 pb-4 border-b">{termsContent.introduction}</p>
                        )}
                        {termsContent.sections?.map((section, idx) => (
                          <div key={idx} className="mb-4 pb-4 border-b border-gray-100 last:border-0">
                            <h4 className="font-semibold text-[#003456] text-sm mb-2">
                              {section.number}. {section.title}
                            </h4>
                            <p className="text-xs text-gray-600 mb-2">{section.content}</p>
                            
                            {/* Points list */}
                            {section.points && (
                              <ul className="ml-4 space-y-1 mb-2">
                                {section.points.map((point, i) => (
                                  <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                    <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{point}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Subsections */}
                            {section.subsections && (
                              <div className="ml-4 mt-2 space-y-2">
                                {section.subsections.map((sub, i) => (
                                  <div key={i} className="border-l-2 border-blue-200 pl-3">
                                    <p className="text-xs font-medium text-gray-700">{sub.title}</p>
                                    <p className="text-xs text-gray-600">{sub.content}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Example box */}
                            {section.example && (
                              <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2">
                                <p className="text-xs text-blue-800"><strong>Example:</strong> {section.example}</p>
                              </div>
                            )}
                            
                            {/* Warning box */}
                            {section.warning && (
                              <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                                <p className="text-xs text-amber-800"><strong>Note:</strong> {section.warning}</p>
                              </div>
                            )}
                            
                            {/* Prohibited activities */}
                            {section.prohibited && (
                              <ul className="ml-4 mt-2 space-y-1">
                                {section.prohibited.map((item, i) => (
                                  <li key={i} className="text-xs text-red-700 flex items-start gap-2">
                                    <X className="h-3 w-3 text-red-500 flex-shrink-0 mt-0.5" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Note */}
                            {section.note && (
                              <p className="mt-2 text-xs text-gray-500 italic">{section.note}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="border-t p-3 bg-gray-50">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={termsRead}
                          onChange={(e) => setTermsRead(e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300"
                          data-testid="terms-checkbox"
                        />
                        <span className="text-sm text-gray-700">
                          I have read and agree to the Affiliate Terms & Conditions
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Document 2: Disclosure Requirements */}
                  <div className="mb-4 border rounded-xl overflow-hidden">
                    <button 
                      onClick={() => setExpandedOptInSection(expandedOptInSection === 'disclosure' ? null : 'disclosure')}
                      className={`w-full flex items-center justify-between p-4 transition-colors ${
                        disclosureRead ? 'bg-green-50 hover:bg-green-100' : 'bg-purple-50 hover:bg-purple-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Shield className={`h-6 w-6 ${disclosureRead ? 'text-green-600' : 'text-purple-600'}`} />
                        <div className="text-left">
                          <span className="text-lg font-bold text-gray-800">2. Disclosure Requirements (US & Canada)</span>
                          <p className="text-sm text-gray-500">FTC and Competition Bureau compliance guidelines</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {disclosureRead && <CheckCircle className="h-5 w-5 text-green-600" />}
                        {expandedOptInSection === 'disclosure' ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </div>
                    </button>
                    
                    {expandedOptInSection === 'disclosure' && disclosureContent && (
                      <div className="border-t max-h-[400px] overflow-y-auto p-4 bg-white">
                        {disclosureContent.overview && (
                          <p className="text-sm text-gray-600 mb-4 pb-3 border-b">{disclosureContent.overview}</p>
                        )}
                        {disclosureContent.introduction && (
                          <p className="text-xs text-gray-600 mb-3">{disclosureContent.introduction}</p>
                        )}
                        {disclosureContent.sections?.map((section, idx) => (
                          <div key={idx} className="mb-4 pb-4 border-b border-gray-100 last:border-0">
                            <h4 className="font-semibold text-purple-700 text-sm mb-2">{section.title}</h4>
                            <p className="text-xs text-gray-600 mb-2">{section.content}</p>
                            
                            {/* Requirements */}
                            {section.requirements && (
                              <ul className="ml-4 space-y-1 mb-2">
                                {section.requirements.map((req, i) => (
                                  <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                    <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Placements */}
                            {section.placements && (
                              <ul className="ml-4 space-y-1 mb-2">
                                {section.placements.map((p, i) => (
                                  <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                    <CheckCircle className="h-3 w-3 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <span>{p}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Acceptable hashtags */}
                            {section.acceptable_hashtags && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {section.acceptable_hashtags.map((tag, i) => (
                                  <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                            
                            {/* Responsibilities */}
                            {section.responsibilities && (
                              <ul className="ml-4 space-y-1 mb-2">
                                {section.responsibilities.map((r, i) => (
                                  <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                                    <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span>{r}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            
                            {/* Penalties */}
                            {section.penalties && (
                              <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                                <ul className="space-y-1">
                                  {section.penalties.map((p, i) => (
                                    <li key={i} className="text-xs text-red-700 flex items-start gap-2">
                                      <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0 mt-0.5" />
                                      <span>{p}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Warning */}
                            {section.warning && (
                              <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                                <p className="text-xs text-amber-800">{section.warning}</p>
                              </div>
                            )}
                            
                            {/* Note */}
                            {section.note && (
                              <p className="mt-2 text-xs text-gray-500 italic">{section.note}</p>
                            )}
                          </div>
                        ))}
                        
                        {/* Sample Disclosures */}
                        {disclosureContent.sample_disclosures && (
                          <div className="mt-4 pt-4 border-t">
                            <h4 className="font-semibold text-purple-700 text-sm mb-3">Sample Disclosures</h4>
                            <div className="space-y-2">
                              {disclosureContent.sample_disclosures.map((sample, i) => (
                                <div key={i} className="bg-gray-50 rounded p-2">
                                  <p className="text-xs font-medium text-gray-700 mb-1">{sample.type}:</p>
                                  <p className="text-xs text-gray-600 italic">"{sample.text}"</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="border-t p-3 bg-gray-50">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={disclosureRead}
                          onChange={(e) => setDisclosureRead(e.target.checked)}
                          className="w-5 h-5 rounded border-gray-300"
                          data-testid="disclosure-checkbox"
                        />
                        <span className="text-sm text-gray-700">
                          I have read and will comply with the US (FTC) & Canada (Competition Bureau) Disclosure Requirements
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Summary of what you're agreeing to */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">By joining the HomeLyph Affiliate Program, you agree to:</p>
                        <ul className="text-xs text-amber-700 mt-2 space-y-1">
                          <li>• Market HomeLyph products honestly and accurately</li>
                          <li>• Always disclose your affiliate relationship as required by law</li>
                          <li>• Follow program rules regarding commission, payouts, and marketing</li>
                          <li>• Acknowledge that acceptance timestamps are recorded for legal purposes</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Footer with Apply Button */}
            <div className="border-t p-6 bg-gray-50">
              <div className="flex items-center gap-3 mb-4">
                {termsRead && disclosureRead ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <Info className="h-5 w-5 text-gray-400" />
                )}
                <span className={`text-sm ${termsRead && disclosureRead ? 'text-green-700' : 'text-gray-500'}`}>
                  {termsRead && disclosureRead 
                    ? 'Both documents accepted – Ready to apply!' 
                    : `${2 - (termsRead ? 1 : 0) - (disclosureRead ? 1 : 0)} document(s) remaining to review`
                  }
                </span>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowOptInModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAcceptAndApply}
                  disabled={!termsRead || !disclosureRead || submitting}
                  className="flex-1 bg-[#003456] hover:bg-[#001d30] text-white disabled:opacity-50"
                  data-testid="accept-and-apply-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'I Accept – Submit Application'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default AffiliateProgram;
